import { useEffect, useState } from 'react';

import { authState } from '../../services/auth-state';

/**
 * True while the session that *already existed* when this page mounted is still
 * active. Goes false the moment that session ends, and never comes back.
 *
 * For token-link pages (`/setup`, `/reset-password`) this is the question that
 * matters — not the plain "is anyone logged in?". Those flows log the visitor in
 * as their final step (`setupUser` calls `authState.setLoggedIn(true)`), so a
 * live logged-in flag would flip back to true mid-flow and re-block the page
 * right as it should be showing the MFA step. Snapshotting at mount separates
 * "someone else's session is in the way" from "the session this page created".
 *
 * `App` awaits `loadLoggedInState()` before rendering the router, so the value
 * is already accurate on the first render.
 */
export const usePreexistingSession = (): boolean => {
  const [active, setActive] = useState(() => authState.isLoggedIn());

  useEffect(() => {
    if (!active) {
      // Nothing left to watch: any session created from here on is this page's own.
      return;
    }

    const subscriber = (loggedIn: boolean) => {
      if (!loggedIn) {
        setActive(false);
      }
    };

    authState.registerSubscriber(subscriber);
    // Re-check: the logout may have landed between render and this effect.
    if (!authState.isLoggedIn()) {
      setActive(false);
    }

    return () => authState.unregisterSubscriber(subscriber);
  }, [active]);

  return active;
};
